// You — profile per canon ProfileScreen (screens-social.jsx).
// Hero card (since/name + stats chips) → tappable people card → tappable venues card
// → compact palette switcher → bottom nav.
// No scroll. No badges (per user direction). Tap people/venues → full-screen routes.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import {
  type CurrentUser,
  type FriendRequestDto,
  type NightDetailsDto,
  authApi,
  friendsApi,
  meApi,
} from '../api'
import { EditProfileSheet } from '../components/EditProfileSheet'
import { Icon } from '../components/Icon'
import { Toast } from '../components/Toast'
import { TopBar } from '../components/TopBar'

export const Route = createFileRoute('/you')({
  component: YouRoute,
})

type Palette = 'poster' | 'midnight' | 'faded'

const PALETTES: { id: Palette; label: string }[] = [
  { id: 'poster', label: 'poster' },
  { id: 'midnight', label: 'midnight' },
  { id: 'faded', label: 'faded' },
]

const AVATAR_COLORS = [
  'var(--pink)',
  'var(--lime)',
  'var(--teal)',
  'var(--orange)',
  'var(--plum)',
  'var(--acid)',
] as const

function avatarColor(seed: string | null | undefined): string {
  if (!seed) return AVATAR_COLORS[0]
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]
}

function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const trimmed = name.trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

type Status =
  | { kind: 'loading' }
  | {
      kind: 'ready'
      user: CurrentUser
      nights: NightDetailsDto[]
      friends: FriendRequestDto[] // accepted friendships only
    }

function YouRoute() {
  const navigate = useNavigate()
  const [state, setState] = useState<Status>({ kind: 'loading' })
  const [palette, setPalette] = useState<Palette>('poster')
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [editing, setEditing] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable; we intentionally run once on mount.
  useEffect(() => {
    void (async () => {
      const me = await authApi.me()
      if (!me) {
        navigate({ to: '/login' })
        return
      }
      const [nights, requests] = await Promise.all([meApi.history(), friendsApi.requests()])
      const acceptedIncoming = requests.incoming.filter((r) => r.status === 'accepted')
      const acceptedOutgoing = requests.outgoing.filter((r) => r.status === 'accepted')
      const friends = [...acceptedIncoming, ...acceptedOutgoing]
      setState({ kind: 'ready', user: me, nights, friends })

      try {
        const stored = localStorage.getItem('stumble.palette')
        if (stored === 'poster' || stored === 'midnight' || stored === 'faded') {
          setPalette(stored)
        }
      } catch {
        /* ignore */
      }
    })()
  }, [])

  function setPaletteAndPersist(p: Palette) {
    setPalette(p)
    try {
      localStorage.setItem('stumble.palette', p)
    } catch {
      /* ignore */
    }
    const app = document.querySelector('.stumble-app')
    if (app) app.setAttribute('data-palette', p)
  }

  async function doSignOut() {
    setSigningOut(true)
    try {
      await authApi.logout()
    } finally {
      setSigningOut(false)
      navigate({ to: '/login' })
    }
  }

  function applyProfileUpdate(user: CurrentUser) {
    setState((prev) => (prev.kind === 'ready' ? { ...prev, user } : prev))
  }

  function showToast(message: string) {
    setToast(message)
    window.setTimeout(() => setToast((t) => (t === message ? null : t)), 2200)
  }

  // Derive stats client-side from /api/me/nights — no /api/me/stats endpoint yet.
  const stats = useMemo(() => {
    if (state.kind !== 'ready') return { nights: 0, venues: 0, drinks: 0 }
    const venueIds = new Set<string>()
    let drinks = 0
    for (const n of state.nights) {
      drinks += n.totals.drinks
      for (const v of n.totals.byVenue) venueIds.add(v.venueId)
    }
    return { nights: state.nights.length, venues: venueIds.size, drinks }
  }, [state])

  const sinceLabel = useMemo(() => {
    if (state.kind !== 'ready') return ''
    // No createdAt on CurrentUser — use earliest night start, or fallback.
    const earliest = state.nights.reduce<Date | null>((acc, n) => {
      const d = new Date(n.night.startedAt)
      return acc == null || d < acc ? d : acc
    }, null)
    if (!earliest) return 'new'
    return earliest.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toLowerCase()
  }, [state])

  if (state.kind === 'loading') {
    return (
      <div className="stumble-screen">
        <TopBar eyebrow="you" title="profile" />
        <div
          className="screen-body with-nav body muted"
          style={{ textAlign: 'center', alignContent: 'center' }}
          data-testid="you-loading"
        >
          loading…
        </div>
      </div>
    )
  }

  const { user, friends } = state
  const displayName = (user.displayName || 'friend').toLowerCase()
  const friendCount = friends.length
  const top3 = friends.slice(0, 3).map((req) => {
    // Show the OTHER user in the friendship.
    const other = req.requesterId === user.id ? req.addressee : req.requester
    return {
      id: other.id,
      name: other.displayName,
      color: avatarColor(other.friendCode ?? other.id),
      letter: initials(other.displayName).slice(0, 1),
    }
  })

  const heroAvatarColor = avatarColor(user.friendCode ?? user.id)
  const heroInitial = initials(displayName).slice(0, 1)

  return (
    <div className="stumble-screen">
      <TopBar
        eyebrow="you"
        title="profile"
        right={
          <button
            type="button"
            className="icon-btn"
            onClick={() => setConfirmSignOut(true)}
            aria-label="Sign out"
            data-testid="you-signout-trigger"
            style={{ background: 'var(--paper)' }}
          >
            <Icon name="more" size={18} />
          </button>
        }
      />
      <div
        className="screen-body with-nav"
        style={{ gridTemplateRows: 'auto auto auto auto auto', gap: 12 }}
      >
        {/* Hero card — avatar + since + display name + drinks tally */}
        <div
          className="profile-hero"
          data-testid="you-hero"
          style={{
            background: `linear-gradient(105deg, ${heroAvatarColor} 0%, ${heroAvatarColor} 55%, rgba(0,0,0,0.05) 100%)`,
          }}
        >
          <span className="profile-avatar" aria-hidden style={{ background: 'var(--paper)' }}>
            <span style={{ color: 'var(--ink)' }}>{heroInitial}</span>
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <span className="eyebrow" style={{ color: 'var(--paper)' }}>
              since {sinceLabel}
            </span>
            <div
              className="display"
              data-testid="you-display-name"
              style={{
                fontSize: 44,
                lineHeight: 0.88,
                color: 'var(--paper)',
                textShadow: '0 3px 0 var(--shadow-ink)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {displayName}
              </span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                aria-label="Edit profile"
                data-testid="you-edit-profile"
                className="profile-edit-btn"
              >
                <Icon name="edit" size={16} />
              </button>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono" style={{ fontSize: 28, lineHeight: 1, color: 'var(--paper)' }}>
              {stats.drinks}
            </div>
            <div className="eyebrow" style={{ color: 'var(--paper)', fontSize: 9 }}>
              drinks
            </div>
          </div>
        </div>

        {/* Stats chips */}
        <div className="row" style={{ gap: 6 }}>
          <StatChip value={stats.nights} label="nights" />
          <StatChip value={stats.venues} label="venues" />
          <StatChip value={friendCount} label="friends" />
        </div>

        {/* Your people — tappable hero card */}
        <button
          type="button"
          onClick={() => navigate({ to: '/your-people' })}
          className="people-card"
          data-testid="you-people-card"
        >
          <div className="people-card-stack">
            {top3.length > 0 ? (
              <>
                {top3.map((f, i) => (
                  <span
                    key={f.id}
                    className="people-avatar"
                    style={{ background: f.color, marginLeft: i === 0 ? 0 : -14, zIndex: 10 - i }}
                  >
                    {f.letter}
                  </span>
                ))}
                {friendCount > 3 && (
                  <span
                    className="people-avatar people-more"
                    style={{ marginLeft: -14, zIndex: 0 }}
                  >
                    +{friendCount - 3}
                  </span>
                )}
              </>
            ) : (
              <span
                className="people-avatar"
                style={{ background: 'var(--paper)', color: 'var(--ink)' }}
              >
                ?
              </span>
            )}
          </div>
          <div className="people-meta">
            <div className="eyebrow">your people</div>
            <div className="people-title">
              {friendCount === 0
                ? 'add friends'
                : `${friendCount} stumbler${friendCount === 1 ? '' : 's'}`}
            </div>
            <div className="body-sm muted" style={{ fontSize: 12 }}>
              {friendCount === 0
                ? 'find your crew so the board fills up'
                : 'tap to see who, when, and how much damage'}
            </div>
          </div>
          <span className="people-arrow" aria-hidden>
            <Icon name="back" size={20} />
          </span>
        </button>

        {/* Your venues — tappable hero card (pink) */}
        <button
          type="button"
          onClick={() => navigate({ to: '/your-venues' })}
          className="people-card venues-card"
          data-testid="you-venues-card"
        >
          <div className="people-card-stack venues-thumbs">
            {stats.venues > 0 ? (
              <>
                {[0, 1, 2].slice(0, Math.min(3, stats.venues)).map((i) => (
                  <span
                    key={`v-${i}`}
                    className="venue-thumb"
                    style={{
                      background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                      marginLeft: i === 0 ? 0 : -12,
                      zIndex: 10 - i,
                    }}
                  />
                ))}
                {stats.venues > 3 && (
                  <span className="people-avatar people-more" style={{ marginLeft: -12 }}>
                    +{stats.venues - 3}
                  </span>
                )}
              </>
            ) : (
              <span
                className="people-avatar"
                style={{ background: 'var(--paper)', color: 'var(--ink)' }}
              >
                ?
              </span>
            )}
          </div>
          <div className="people-meta">
            <div className="eyebrow">your venues</div>
            <div className="people-title">
              {stats.venues === 0
                ? 'no spots yet'
                : `${stats.venues} place${stats.venues === 1 ? '' : 's'}`}
            </div>
            <div className="body-sm muted" style={{ fontSize: 12 }}>
              {stats.venues === 0
                ? 'log a stop to start your map'
                : 'tap to see every spot and the damage'}
            </div>
          </div>
          <span className="people-arrow" aria-hidden>
            <Icon name="back" size={20} />
          </span>
        </button>

        {/* Compact palette row */}
        <div className="row" style={{ gap: 6, alignItems: 'center' }} data-testid="you-palette-row">
          <span className="eyebrow" style={{ marginRight: 2 }}>
            palette
          </span>
          {PALETTES.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setPaletteAndPersist(p.id)}
              className="chip"
              data-testid={`you-palette-${p.id}`}
              style={{
                flex: 1,
                justifyContent: 'center',
                minHeight: 38,
                background: palette === p.id ? 'var(--ink)' : 'var(--paper)',
                color: palette === p.id ? 'var(--paper)' : 'var(--ink)',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {confirmSignOut && (
        <SignOutSheet
          busy={signingOut}
          onCancel={() => setConfirmSignOut(false)}
          onConfirm={doSignOut}
        />
      )}

      {editing && (
        <EditProfileSheet
          user={user}
          onCancel={() => setEditing(false)}
          onSaved={(u) => {
            applyProfileUpdate(u)
            setEditing(false)
            showToast('updated')
          }}
        />
      )}

      <Toast show={toast !== null} onDismiss={() => setToast(null)}>
        {toast}
      </Toast>

      <style>{`
        .profile-hero {
          position: relative;
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 12px;
          align-items: center;
          padding: 12px 14px;
          border: 3px solid var(--line);
          border-radius: 18px;
          box-shadow: 0 6px 0 var(--shadow-ink);
          color: var(--paper);
          min-height: 0;
        }
        .profile-avatar {
          width: 56px; height: 56px;
          border-radius: 999px;
          border: 2.5px solid var(--line);
          display: grid; place-items: center;
          font-family: var(--font-display);
          font-size: 26px;
          line-height: 1;
        }
        .people-card {
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 14px;
          align-items: center;
          padding: 12px 14px;
          border: 3px solid var(--line);
          border-radius: 20px;
          background: var(--lime);
          color: var(--ink);
          box-shadow: 0 6px 0 var(--shadow-ink);
          text-align: left;
          min-height: 0;
        }
        .venues-card { background: var(--pink); color: var(--paper); }
        .stumble-app[data-palette="midnight"] .people-card { color: #0f0d18; }
        .people-card:active { transform: translateY(3px); box-shadow: 0 3px 0 var(--shadow-ink); }
        .people-card-stack { display: flex; align-items: center; }
        .people-avatar {
          width: 40px; height: 40px;
          border-radius: 999px;
          color: var(--paper);
          border: 2.5px solid var(--line);
          display: grid; place-items: center;
          font-family: var(--font-display);
          font-size: 16px;
        }
        .people-more {
          background: var(--paper) !important;
          color: var(--ink) !important;
          font-size: 13px !important;
        }
        .venue-thumb {
          width: 40px; height: 40px;
          border-radius: 10px;
          border: 2.5px solid var(--line);
        }
        .venues-thumbs { gap: 0; }
        .people-meta { display: grid; gap: 2px; min-width: 0; }
        .people-title {
          font-family: var(--font-display);
          font-size: 26px;
          line-height: 0.95;
          text-transform: lowercase;
        }
        .venues-card .body-sm.muted { color: var(--paper); opacity: 0.85; }
        .people-arrow {
          width: 36px; height: 36px;
          border-radius: 999px;
          background: var(--paper);
          color: var(--ink);
          border: 2px solid var(--line);
          display: grid; place-items: center;
          transform: scaleX(-1);
        }
        .profile-edit-btn {
          flex: 0 0 auto;
          width: 28px; height: 28px;
          border-radius: 999px;
          background: var(--paper);
          color: var(--ink);
          border: 2px solid var(--line);
          display: grid; place-items: center;
          padding: 0;
          cursor: pointer;
          box-shadow: 0 2px 0 var(--shadow-ink);
        }
        .profile-edit-btn:active { transform: translateY(2px); box-shadow: 0 0 0 var(--shadow-ink); }
      `}</style>
    </div>
  )
}

function StatChip({ value, label }: { value: number; label: string }) {
  const chipStyle: CSSProperties = {
    flex: 1,
    display: 'grid',
    placeItems: 'center',
    gap: 2,
    padding: '10px 6px',
    border: '2.5px solid var(--line)',
    borderRadius: 12,
    background: 'var(--paper)',
    boxShadow: '0 4px 0 var(--shadow-ink)',
    minHeight: 56,
  }
  return (
    <div style={chipStyle} data-testid={`you-stat-${label}`}>
      <strong className="mono" style={{ fontSize: 22, lineHeight: 1 }}>
        {value}
      </strong>
      <span className="eyebrow" style={{ fontSize: 9 }}>
        {label}
      </span>
    </div>
  )
}

function SignOutSheet({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  // Escape key cancels.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div className="signout-backdrop" data-testid="signout-sheet">
      <button
        type="button"
        className="signout-backdrop-dismiss"
        aria-label="Cancel sign out"
        onClick={onCancel}
        disabled={busy}
      />
      <dialog open aria-modal="true" aria-labelledby="signout-title" className="signout-sheet">
        <div className="eyebrow" id="signout-title" style={{ marginBottom: 6 }}>
          confirm
        </div>
        <div className="display" style={{ fontSize: 30, lineHeight: 0.95, marginBottom: 12 }}>
          sign out of stumble?
        </div>
        <p className="body-sm muted" style={{ fontSize: 13, marginBottom: 14 }}>
          You'll need an OTP code to come back.
        </p>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn ghost full"
            onClick={onCancel}
            disabled={busy}
            data-testid="signout-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn pink full"
            onClick={onConfirm}
            disabled={busy}
            data-testid="signout-confirm"
          >
            {busy ? 'signing out…' : 'Sign out'}
          </button>
        </div>
      </dialog>
      <style>{`
        .signout-backdrop {
          position: absolute;
          inset: 0;
          z-index: 100;
          background: rgba(0,0,0,0.45);
          display: grid;
          align-items: end;
          justify-items: stretch;
          padding: 18px 16px calc(env(safe-area-inset-bottom) + 100px);
        }
        .signout-backdrop-dismiss {
          position: absolute;
          inset: 0;
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
        }
        .signout-sheet {
          position: relative;
          margin: 0;
          width: 100%;
          background: var(--paper);
          color: var(--ink);
          border: 3px solid var(--line);
          border-radius: 22px;
          box-shadow: 0 8px 0 var(--shadow-ink);
          padding: 18px 18px 16px;
        }
      `}</style>
    </div>
  )
}
